<template>
  <v-card v-resize="onResize">
    <v-form>
      <v-container>
        <v-text-field
          autofocus
          ref="searchField"
          v-model.trim="customerContextSearch"
          :label="$t('search_accounts')"
          class="ml-2 mr-5 pb-0"
          prepend-icon="mdi-map-marker">
        </v-text-field>
      </v-container>
    </v-form>
    <v-list :style="`max-height: ${customerListHeight}px`" class="overflow-y-auto">
      <v-list-item-group color="primary-900">
        <v-list-item v-for="customer in fuzzyCustomerMatches" :key="customer.id" @click="changeCustomerContext(customer)">
          <v-list-item-icon>
            <v-icon>{{ customer.parent_id === null ? 'mdi-domain' : 'mdi-map-marker' }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ customer.name }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list-item-group>
    </v-list>
  </v-card>
</template>

<script>
import Fuse from 'fuse.js'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import WindowResizeMixin from '@/components/mixins/WindowResize'
import { get, sortBy, filter, forEach } from 'lodash'

export default {
  name: 'CustomerContextSelector',
  props: {
    heightAdjustment: {
      type: Number,
      default: 140
    }
  },
  data () {
    return {
      customerContextSearch: ''
    }
  },
  mixins: [
    CurrentUserMixin,
    WindowResizeMixin
  ],
  methods: {
    resetSearch () {
      this.customerContextSearch = ''
    },
    changeCustomerContext (customer) {
      this.customerContextSearch = ''
      this.$gtag.event('change', { event_category: 'CustomerContext', event_label: customer.name })
      this.$store.dispatch('userModule/changeCustomerContext', customer.id)
        .then(() => {
          this.$emit('change-context', customer)
        })
    },
    /**
     * Returns an array of customers with the parent specified. If no parent is specified then "root" customers are
     * returned.
     * @var CustomerRecord parent
     * @return array<CustomerRecord>
     */
    getRootSortedCustomers (parent = null) {
      const sortedCustomers = []
      const customersInScope = filter(this.customers, { parent_id: get(parent, 'id', null) })
      const sortByColumn = parent ? 'name' : 'id'
      const sortedByName = sortBy(customersInScope, [function (customer) { return customer[sortByColumn] }])

      forEach(sortedByName, function (customer) {
        sortedCustomers.push(customer)
        const childCustomers = this.getRootSortedCustomers(customer)
        sortedCustomers.push(...childCustomers)
      }.bind(this))

      return sortedCustomers
    }
  },
  computed: {
    customerListHeight () {
      return (this.windowSize.y - this.heightAdjustment)
    },
    /**
     * Returns a list of customers sorted by name
     *
     * @return array
     */
    sortedCustomers: function () {
      if (filter(this.customers, { parent_id: null }).length > 0) {
        return this.getRootSortedCustomers()
      }
      return sortBy(this.customers, [function (customer) { return customer.name }])
    },
    /**
     * Return a set of customer objects that approximately match the customerContextSearch string.
     * @return Array
     */
    fuzzyCustomerMatches: function () {
      // Fuse returns an empty array when given an empty string. Prevent
      // this and return all customers unless we have a term to search on
      if (this.customerContextSearch === '') {
        return this.sortedCustomers
      }

      // These are "hand tweaked" and may require some more finesse, nothing
      // magic about them so don't be afraid to change if you think results
      // aren't being well filtered.
      const options = {
        keys: ['name'],
        shouldSort: false,
        threshold: 0.3
      }

      const fuse = new Fuse(this.sortedCustomers, options)
      return fuse.search(this.customerContextSearch)
    }
  }
}
</script>
